import { Tag } from '@factorialco/deprecated-design-system'
import {
  AttendanceCustomTimeRangeCategory,
  GetCustomTimeRangeCategoriesDocument,
  Maybe,
  TimeSettingsCustomTimeRangeCategory,
  TimeSettingsEnumsTimeTypeEnum,
} from 'generated/resources/graphql'

import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'
import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

export const useFetchCustomTimeRangeCategories = (settingId: number) => {
  const { data: categories, loading } = useQuery(GetCustomTimeRangeCategoriesDocument, {
    variables: {
      attendanceEmployeesSettingId: settingId,
    },
  })

  const timeRangeCategories = new CustomTimeRangeCategoriesCollection(
    categories?.attendance.customTimeRangeCategoriesConnection.nodes || []
  )

  return {
    loading,
    categories: timeRangeCategories,
  }
}

export const useLazyFetchCustomTimeRangeCategories = () => {
  const [fetchCustomTimeRangeCategories, { loading }] = useLazyQuery(
    GetCustomTimeRangeCategoriesDocument
  )

  const fetch = async (settingId: number) => {
    const { data } = await fetchCustomTimeRangeCategories({
      variables: {
        attendanceEmployeesSettingId: settingId,
      },
    })

    const timeRangeCategories = new CustomTimeRangeCategoriesCollection(
      data?.attendance?.customTimeRangeCategoriesConnection?.nodes || []
    )

    return timeRangeCategories
  }

  return { loading, fetch }
}

export class CustomTimeRangeCategoriesCollection {
  customTimeRangeCategories: CustomTimeRangeCategory[]
  constructor(customTimeRangeCategories: AttendanceCustomTimeRangeCategory[]) {
    this.customTimeRangeCategories = customTimeRangeCategories.map((category) => {
      return new CustomTimeRangeCategory(category)
    })
  }

  get groupedCategories() {
    const grouped = this.customTimeRangeCategories.reduce<{
      regularCategories: CustomTimeRangeCategory[]
      overtimeCategories: CustomTimeRangeCategory[]
    }>(
      (acc, category) => {
        if (category.isRegular) {
          acc.regularCategories.push(category)
        } else if (category.isOvertime) {
          acc.overtimeCategories.push(category)
        }
        return acc
      },
      { regularCategories: [], overtimeCategories: [] }
    )

    return grouped
  }

  get hasTimeRangeCategories(): boolean {
    return this.customTimeRangeCategories.length > 0
  }

  get withTimeSettingCategoryArchived(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter(
      (category) => category.timeSettingsCustomTimeRangeCategory?.archived
    )
  }

  get withTimeSettingCategoryActive(): CustomTimeRangeCategory[] {
    return this.customTimeRangeCategories.filter(
      (category) => !category.timeSettingsCustomTimeRangeCategory?.archived
    )
  }

  findCategoryById(id: number): CustomTimeRangeCategory | undefined {
    return this.customTimeRangeCategories.find((category) => category.id === id)
  }

  findCategoryByTimeSettingsId(
    timeSettingsId?: Maybe<number>
  ): CustomTimeRangeCategory | undefined {
    if (!timeSettingsId) return undefined

    return this.customTimeRangeCategories.find(
      (category) => category.timeSettingsCustomTimeRangeCategoryId === timeSettingsId
    )
  }
}

export class CustomTimeRangeCategory {
  attendanceEmployeesSettingId: number
  id: number
  name: string
  timeType: TimeSettingsEnumsTimeTypeEnum
  rawMinutesInCents: number
  equivalentMinutesInCents: number
  timeSettingsCustomTimeRangeCategoryId: number
  archived: Maybe<boolean> | undefined
  timeSettingsCustomTimeRangeCategory: Maybe<TimeSettingsCustomTimeRangeCategory> | undefined

  constructor(category: AttendanceCustomTimeRangeCategory) {
    this.attendanceEmployeesSettingId = category.attendanceEmployeesSettingId
    this.id = category.id
    this.name = category.name
    this.timeType = category.timeType
    this.rawMinutesInCents = category.rawMinutesInCents
    this.equivalentMinutesInCents = category.equivalentMinutesInCents
    this.timeSettingsCustomTimeRangeCategoryId = category.timeSettingsCustomTimeRangeCategoryId
    this.archived = category.timeSettingsCustomTimeRangeCategory?.archived
    this.timeSettingsCustomTimeRangeCategory = category.timeSettingsCustomTimeRangeCategory
  }

  get isRegular() {
    return this.timeType === 'regular'
  }

  get isOvertime() {
    return this.timeType === 'overtime'
  }

  get timeTypeIsAnyTime() {
    return this.timeType === 'any_time'
  }

  get rawMinutes() {
    return minutesToText(this.rawMinutesInCents / 100)
  }

  get equivalentMinutes() {
    return minutesToText(this.equivalentMinutesInCents / 100)
  }

  get tagType() {
    if (this.isRegular) return Tag.Types.grey
    if (this.isOvertime) return Tag.Types.tangerine

    return Tag.Types.purple
  }

  get tagLabel() {
    if (this.isRegular)
      return i18n.t('time_tracking.settings.time_blocks.worked_time_categories.regular')
    if (this.isOvertime)
      return i18n.t('time_tracking.settings.time_blocks.worked_time_categories.overtime')

    return i18n.t('time_tracking.settings.time_blocks.worked_time_categories.any_time')
  }
}
